<template>
  <!-- Collapsed State: profile + name + expand icon -->
  <div
    v-if="!expanded"
    class="embedded_chat_topbar flex flex-row items-center gap-2 px-3 py-2 max-h-[45px] flex-shrink-0 cursor-pointer select-none rounded-t-lg"
    @click="$emit('expand')"
  >
    <template v-if="chatProfileData">
      <div
        v-if="chatProfileData?.isMultiUser"
        class="flex flex-row items-center -space-x-1"
      >
        <ProfileIcon
          v-for="(user, index) in chatProfileData?.users"
          :key="`${user?.id}_profile_${index}`"
          :data="user"
          :font="11"
          :size="22"
          class="border-2 border-[var(--background-color)] rounded-full"
        />
      </div>
      <div
        v-else-if="isGroup && activeChat?.profile_pic"
        class="shrink-0 w-[22px] h-[22px] rounded-full overflow-hidden"
      >
        <img :src="activeChat.profile_pic" alt="group avatar" class="w-full h-full object-cover" />
      </div>
      <div
        v-else-if="isGroup"
        class="flex items-center justify-center rounded-full shrink-0 w-[22px] h-[22px]"
        :class="getAuthUserProfileColor({ name: activeChat?.name }).class"
        :style="getAuthUserProfileColor({ name: activeChat?.name }).style || {}"
      >
        <ThemeImage
          :icon="imageNames.GROUP"
          alt="group_icon"
          class="size-3"
        />
      </div>
      <ProfileIcon v-else :data="chatProfileData" :font="11" :size="22" />
    </template>
    <span class="flex-1 text-sm font-semibold primary_color truncate">{{ displayName }}</span>
    <div
      v-if="unreadCount > 0"
      class="flex items-center justify-center text-xs text-white font-semibold pt-[1px] px-[4px] leading-[14px] !min-w-[16px] count_tag"
    >
      {{ unreadCount }}
    </div>
    <ThemeImage
      :icon="imageNames.UP_ARROW"
      class="size-3.5 cursor-pointer opacity-40"
      alt="expand"
    />
    <ThemeImage
      :icon="imageNames.CLOSE_GREY"
      class="size-3.5 cursor-pointer"
      alt="close"
      @click.stop="$emit('close')"
    />
  </div>

  <!-- Expanded State: full top bar -->
  <div
    v-else
    class="flex flex-row items-center justify-between px-3 py-2 border_bottom max-h-[45px] flex-shrink-0 cursor-pointer"
    style="background-color: var(--background-color)"
    @click="$emit('collapse')"
  >
    <div class="flex flex-row items-center gap-2 min-w-0">
      <template v-if="chatProfileData">
        <div
          v-if="chatProfileData?.isMultiUser"
          class="flex flex-row items-center -space-x-1"
        >
          <ProfileIcon
            v-for="(user, index) in chatProfileData?.users"
            :key="`${user?.id}_profile_${index}`"
            :data="user"
            :font="11"
            :size="22"
            :class="user?.avatar_url ? 'rounded-full' : 'border-2 border-[var(--background-color)] rounded-full'"
          />
        </div>
        <div
          v-else-if="isGroup && activeChat?.profile_pic"
          class="shrink-0 w-[22px] h-[22px] rounded-full overflow-hidden"
        >
          <img :src="activeChat.profile_pic" alt="group avatar" class="w-full h-full object-cover" />
        </div>
        <div
          v-else-if="isGroup"
          class="flex items-center justify-center rounded-full shrink-0 w-[22px] h-[22px]"
          :class="getAuthUserProfileColor({ name: activeChat?.name }).class"
          :style="getAuthUserProfileColor({ name: activeChat?.name }).style || {}"
        >
          <ThemeImage
            :icon="imageNames.GROUP"
            alt="group_icon"
            class="size-3"
          />
        </div>
        <ProfileIcon v-else :data="chatProfileData" :font="11" :size="22" />
      </template>
      <span
        class="text-sm font-semibold primary_color truncate"
        :class="{ 'cursor-pointer': canOpenGroupDetails }"
        @click="handleNameClick($event)"
      ><template v-if="isGroup && isPrivateGroup"><ThemeImage :icon="imageNames.LOCK_GREY" class="size-3.5 inline-block mr-1 align-middle" alt="private_group_icon" /></template><template v-else-if="isGroup">#&nbsp;</template>{{ displayName }}</span>
    </div>

    <div class="flex flex-row items-center gap-1 shrink-0" @click.stop>
      <a-button
        v-if="showJoinButton"
        class="w-fit secondary_small"
        :loading="isJoining"
        size="small"
        @click="handleJoinGroup"
      >
        Join Group
      </a-button>

      <a-tooltip v-if="showAddMembersButton" title="View members">
        <div
          class="flex flex-row items-center gap-1 px-1.5 py-0.5 rounded cursor-pointer hover:bg-[var(--sidebar-item-selected)] transition-colors"
          @click="handleViewMembers"
        >
          <ThemeImage
            :icon="imageNames.USERS_UNSELECTED"
            class="size-4"
            alt="members_icon"
          />
          <span class="text-xs secondary_color">{{ memberCount }}</span>
        </div>
      </a-tooltip>

      <a-tooltip v-if="showHuddleButton" :title="huddleButtonTitle">
        <div
          class="flex flex-row items-center gap-1 px-1.5 py-0.5 rounded transition-colors"
          :class="[
            hasActiveHuddle
              ? 'bg-green-500/15 hover:bg-green-500/25 cursor-pointer'
              : 'hover:bg-[var(--sidebar-item-selected)] cursor-pointer'
          ]"
          @click="handleHuddleClick()"
        >
          <ThemeImage
            :icon="imageNames.SALES_DIALER_UNSELECTED"
            class="size-4 opacity-70"
            alt="huddle_icon"
          />
          <span v-if="hasActiveHuddle" class="text-xs text-green-600 font-medium">
            {{ huddleParticipantCount }}
          </span>
        </div>
      </a-tooltip>

      <a-dropdown v-if="showMoreOptions" :trigger="['click']">
        <a-button class="w-fit secondary_small" size="small">
          <ThemeImage
            :icon="imageNames.MORE_VERTICAL"
            class="size-4"
            alt="more_options_icon"
          />
        </a-button>
        <template #overlay>
          <a-menu>
            <a-menu-item v-if="showAddMembersButton" key="add-members" @click="handleViewMembers">
              <div class="flex flex-row items-center gap-2">
                <ThemeImage :icon="imageNames.ADD_GREY" class="size-4" alt="add_grey_icon" />
                <span>Add people to group</span>
              </div>
            </a-menu-item>
            <a-menu-item v-if="!isDM && !isHomeGroup && isCurrentUserAdmin" key="update-group" @click="handleUpdateGroup">
              <div class="flex flex-row items-center gap-2">
                <ThemeImage :icon="imageNames.EDIT" class="size-4" alt="edit_icon" />
                <span>Update group</span>
              </div>
            </a-menu-item>
            <a-menu-item key="star-chat" @click="handleTogglePin">
              <div class="flex flex-row items-center gap-2">
                <ThemeImage :icon="imageNames.STAR_GREY" class="size-4" alt="pin_icon" />
                <span>{{ isCurrentChatPinned ? 'Unstar conversation' : 'Star conversation' }}</span>
              </div>
            </a-menu-item>
            <a-menu-item v-if="!isOneOnOneDM && !isHomeGroup && isCurrentUserAdmin" key="delete-group" @click="handleDeleteGroup">
              <div class="flex flex-row items-center gap-2">
                <ThemeImage :icon="imageNames.DELETE_GREY" class="size-4" alt="delete_icon" />
                <span>Delete group</span>
              </div>
            </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>

      <ThemeImage
        :icon="imageNames.BOTTOM_ARROW"
        class="size-4 cursor-pointer ml-1 opacity-40"
        alt="collapse"
        @click="$emit('collapse')"
      />
      <ThemeImage
        :icon="imageNames.CLOSE_GREY"
        class="size-4 cursor-pointer"
        alt="close"
        @click="$emit('close')"
      />
    </div>

    </div>

  <GroupDetailsModal
    v-model:visible="showGroupDetailsModal"
    :group="activeChat"
    :initial-tab="initialModalTab"
  />

  <AddOrEditGroupModal
    source="embedded_topbar"
    v-model:visible="showEditGroupModal"
    :edit="true"
    :data="activeChat"
  />

  <ConfirmationModal
    :visible="showDeleteModal"
    :title="`Delete ${activeChat?.name}?`"
    :message="`Are you sure you want to delete this group?`"
    width="30vw"
    position="top"
    type="delete"
    confirmText="Delete"
    cancelText="Cancel"
    loadingText="Deleting..."
    :loading="isDeleting"
    @confirm="handleDeleteConfirm"
    @cancel="handleDeleteCancel"
    @update:visible="handleDeleteModalClose"
  />
</template>

<script setup>
import { computed, ref, toRef, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import eventBus from "@/src/utils/eventBus.js";
import ChatService from "@/src/api/ChatService.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import GroupDetailsModal from "@/src/components/projects_components/chats/modals/GroupDetailsModal.vue";
import AddOrEditGroupModal from "@/src/components/projects_components/chats/modals/AddOrEditGroupModal.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { chatGroupVisibilityOptions, chatTypes, chatRecipientTypes, HOME_GROUP_NAME } from "@/src/constants/chatConstants.js";
import { renderEntity, getAuthUserProfileColor } from "@/src/utils/utils.js";
import { useDMResolution } from "@/src/composables/useDMResolution.js";

const store = useStore();
const { resolveOtherDMUserName } = useDMResolution();

const props = defineProps({
  groupMembers: { type: Array, default: () => [] },
  totalMemberCount: { type: Number, default: 0 },
  activeChat: { type: Object, default: null },
  expanded: { type: Boolean, default: true },
});

defineEmits(["collapse", "expand", "close"]);

const showGroupDetailsModal = ref(false);
const initialModalTab = ref(0);
const showEditGroupModal = ref(false);
const showDeleteModal = ref(false);
const isDeleting = ref(false);
const isJoining = ref(false);

const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const activeChat = toRef(props, "activeChat");

const unreadCount = computed(() => {
  if (!activeChat.value?.id) return 0;
  const groups = store.getters["chats/groups"] || [];
  const normalizedId = String(activeChat.value.id).replace(/^group_/, '');
  const group = groups.find((g) => String(g.id).replace(/^group_/, '') === normalizedId);
  return group?.unread_count || 0;
});

const isSelfDM = computed(() => {
  if (!activeChat.value) return false;
  if (activeChat.value.type === chatTypes.SELF_DM) return true;
  if (!isOneOnOneDM.value) return false;
  if (activeChat.value?.is_placeholder) {
    const otherUser = activeChat.value.other_user || activeChat.value.user_data;
    return Number(otherUser?.id) === Number(currentUserId.value);
  }
  if (props.groupMembers.length > 0) {
    return !props.groupMembers.some(
      (m) => Number(m.user_id) !== Number(currentUserId.value)
    );
  }
  if (activeChat.value?.name) {
    const currentUser = allUsers.value.find(u => Number(u.id) === Number(currentUserId.value));
    if (currentUser && renderEntity(currentUser).toLowerCase().trim() === activeChat.value.name.toLowerCase().trim()) {
      return true;
    }
  }
  return false;
});

const chatName = computed(() => {
  if (activeChat.value?.is_placeholder) {
    const otherUser = activeChat.value.other_user || activeChat.value.user_data;
    if (otherUser) {
      const name = renderEntity(otherUser);
      return isSelfDM.value ? `${name} (You)` : name;
    }
    return isSelfDM.value ? `${activeChat.value?.name || ""} (You)` : activeChat.value?.name || "";
  }
  if (isSelfDM.value) {
    return `${activeChat.value?.name || ""} (You)`;
  }
  const otherName = resolveOtherDMUserName(activeChat.value);
  if (otherName) return otherName;
  return activeChat.value?.name || "";
});

const enrichWithStoreUser = (user) => {
  if (!user?.id) return user;
  const found = allUsers.value.find((u) => Number(u.id) === Number(user.id));
  return found || user;
};

const chatProfileData = computed(() => {
  if (!activeChat.value) return activeChat.value;

  if (activeChat.value?.is_placeholder) {
    const otherUser = activeChat.value.other_user || activeChat.value.user_data;
    if (otherUser) return enrichWithStoreUser(otherUser);
  }

  if (isDM.value) {
    const actualMembers = props.groupMembers
      .map(m => m.user)
      .filter(Boolean);

    if (actualMembers.length > 0) {
      if (isOneOnOneDM.value) {
        const otherUser = actualMembers.find(
          user => Number(user.id) !== Number(currentUserId.value)
        );
        return enrichWithStoreUser(otherUser || actualMembers[0]);
      }

      if (actualMembers.length === 1) {
        return enrichWithStoreUser(actualMembers[0]);
      }

      return {
        isMultiUser: true,
        users: actualMembers.slice(0, 3).map(enrichWithStoreUser)
      };
    }
  }

  return activeChat.value;
});

const displayName = computed(() => chatName.value);

const isDM = computed(() => {
  if (!activeChat.value) return false;
  return activeChat.value.is_dm === true || activeChat.value.type === chatTypes.DM || activeChat.value.type === chatTypes.SELF_DM;
});

const isGroup = computed(() => {
  if (!activeChat.value || activeChat.value.is_placeholder) return false;
  return !isDM.value;
});

const isHomeGroup = computed(() => activeChat.value?.name === HOME_GROUP_NAME);

const isOneOnOneDM = computed(() => {
  if (!activeChat.value) return false;
  return (
    activeChat.value.is_dm === true &&
    (activeChat.value.type === chatTypes.DM || activeChat.value.type === chatTypes.SELF_DM) &&
    activeChat.value.recipient_type === chatRecipientTypes.ONE_ON_ONE
  );
});

const isCurrentUserMember = computed(() => {
  if (!activeChat.value) return false;
  if (activeChat.value.is_placeholder) return true;
  if (activeChat.value.type !== chatGroupVisibilityOptions.PUBLIC) return true;
  if (props.groupMembers.length === 0) return true;
  return props.groupMembers.some(
    (m) => Number(m.user_id) === Number(currentUserId.value)
  );
});

const showAddMembersButton = computed(() => {
  if (activeChat.value?.is_placeholder) return false;
  if (!isCurrentUserMember.value) return false;
  return !isOneOnOneDM.value;
});

const showMoreOptions = computed(() => {
  if (!activeChat.value || activeChat.value.is_placeholder) return false;
  if (!isCurrentUserMember.value) return false;
  return true;
});

const isCurrentChatPinned = computed(() => {
  if (!activeChat.value?.id) return false;
  return store.getters["chats/isGroupPinned"](activeChat.value.id);
});

const canOpenGroupDetails = computed(() => {
  if (!activeChat.value || activeChat.value.is_placeholder) return false;
  if (!isCurrentUserMember.value) return false;
  return !isOneOnOneDM.value;
});

const showJoinButton = computed(() => {
  if (!activeChat.value || activeChat.value.is_placeholder) return false;
  if (activeChat.value.type !== chatGroupVisibilityOptions.PUBLIC) return false;
  return !isCurrentUserMember.value;
});

const isPrivateGroup = computed(() => {
  if (!activeChat.value || activeChat.value.is_placeholder) return false;
  if (isDM.value) return false;
  return activeChat.value.type === chatGroupVisibilityOptions.PRIVATE
    || activeChat.value.type === chatGroupVisibilityOptions.EXTERNAL;
});

const memberCount = computed(() => {
  return props.totalMemberCount || props.groupMembers?.length || 0;
});

const isCurrentUserAdmin = computed(() => {
  if (!currentUserId.value || !props.groupMembers?.length) return false;
  const me = props.groupMembers.find(
    (m) => Number(m.user_id) === Number(currentUserId.value)
  );
  if (!me?.role) return false;
  const role = me.role.toUpperCase();
  return role === "ADMIN" || role === "OWNER";
});

const activeHuddle = computed(() => store.getters["huddles/activeHuddle"]);
const isInHuddle = computed(() => store.getters["huddles/isConnected"]);
const huddleParticipantCount = computed(() => store.getters["huddles/participantCount"]);
const isHuddleJoining = computed(() => store.getters["huddles/isJoining"]);

const hasActiveHuddle = computed(() => {
  if (!activeHuddle.value) return false;
  return String(activeHuddle.value.group_id) === String(activeChat.value?.id);
});

const showHuddleButton = computed(() => {
  if (!localStorage.getItem("huddle")) return false;
  if (!activeChat.value || activeChat.value.is_placeholder) return false;
  if (isSelfDM.value) return false;
  if (!isCurrentUserMember.value) return false;
  if (memberCount.value <= 1) return false;
  return true;
});

const huddleButtonTitle = computed(() => {
  if (isHuddleJoining.value) return "Joining...";
  if (isInHuddle.value && hasActiveHuddle.value) return "In Call";
  if (hasActiveHuddle.value) return "Join Call";
  return "Call";
});

let huddleClickCooldown = false;

const handleHuddleClick = async () => {
  try {
    if (isHuddleJoining.value) return;
    if (huddleClickCooldown) return;

    if (isInHuddle.value && hasActiveHuddle.value) {
      return;
    }

    if (isInHuddle.value && !hasActiveHuddle.value) {
      message.warning("You are already in a huddle. Leave the current huddle first.");
      return;
    }

    huddleClickCooldown = true;
    setTimeout(() => { huddleClickCooldown = false; }, 5000);

    let response;
    if (hasActiveHuddle.value && activeHuddle.value?.session_id) {
      response = await store.dispatch("huddles/joinHuddle", {
        sessionId: activeHuddle.value.session_id,
        groupId: activeChat.value?.id,
      });
    } else {
      const memberIds = props.groupMembers
        .map((m) => Number(m.user_id || m.id))
        .filter(Boolean);
      response = await store.dispatch("huddles/startHuddle", {
        groupId: activeChat.value.id,
        memberIds,
      });
    }

    if (response === null) {
      huddleClickCooldown = false;
      const huddleError = store.getters["huddles/error"];
      message.warning(huddleError || "Microphone access is required for huddles");
    }
  } catch (error) {
    huddleClickCooldown = false;
    if (error?.response?.status === 409) {
      await store.dispatch("huddles/fetchActiveHuddle", {
        groupId: activeChat.value.id,
      });
      message.info("A huddle is already active. Click again to join.");
    } else {
      message.error("Failed to start huddle");
    }
  }
};

const handleViewMembers = () => {
  initialModalTab.value = 1;
  showGroupDetailsModal.value = true;
};

const handleNameClick = (event) => {
  if (!canOpenGroupDetails.value) return;
  event.stopPropagation();
  initialModalTab.value = 0;
  showGroupDetailsModal.value = true;
};

const handleJoinGroup = async () => {
  try {
    if (!activeChat.value?.id || !currentUserId.value) return;

    isJoining.value = true;
    const response = await ChatService.addGroupMember(activeChat.value.id, currentUserId.value);

    if (response?.success) {
      message.success("You have joined the group!");
      eventBus.emit("group-members-updated");
      store.dispatch("embeddedChats/fetchGroupMembers", activeChat.value.id);
    }
  } catch (error) {
    console.error("An error occurred while joining group: ", error);
    message.error("Failed to join group!");
  } finally {
    isJoining.value = false;
  }
};

const handleTogglePin = async () => {
  try {
    if (!activeChat.value?.id) return;
    const response = await store.dispatch("chats/togglePinGroup", activeChat.value.id);
    if (response?.success) {
      message.success(response.pinned ? "Conversation starred" : "Conversation unstarred");
    }
  } catch (error) {
    console.error("Failed to toggle pin:", error);
    message.error("Failed to update star");
  }
};

const handleUpdateGroup = () => {
  showEditGroupModal.value = true;
};

const handleDeleteGroup = () => {
  showDeleteModal.value = true;
};

const handleDeleteConfirm = async () => {
  try {
    if (!activeChat.value?.id) return;

    isDeleting.value = true;
    const response = await ChatService.deleteGroup(activeChat.value.id);

    if (response?.success) {
      message.success("Group deleted successfully!");
      showDeleteModal.value = false;
      store.dispatch("embeddedChats/closeChat", activeChat.value.id);
      eventBus.emit("group-deleted");
    }
  } catch (error) {
    console.error("An error occurred while deleting group: ", error);
    message.error("An error occurred while deleting group!");
  } finally {
    isDeleting.value = false;
  }
};

const handleDeleteCancel = () => {
  showDeleteModal.value = false;
};

const handleDeleteModalClose = (val) => {
  if (!val) {
    showDeleteModal.value = false;
  }
};

watch(showGroupDetailsModal, (newVal) => {
  if (!newVal) {
    initialModalTab.value = 0;
  }
});

onMounted(() => {
  if (!allUsers.value || allUsers.value.length === 0) {
    store.dispatch("users/fetchUsers");
  }
});
</script>

<style scoped>
.embedded_chat_topbar {
  background-color: var(--background-color);
  box-shadow: var(--dropdown-boxshadow);
  border: 1px solid var(--white-opacity-8);
}
</style>